import React, { useMemo, useRef, useState } from 'react';

import { useDisclosure } from '../../../hooks';
import { Option, UseSelectProps, UseSelectReturn } from '../types';

/**
 * Hook to create Selection process (single and multiple)
 */
export const useSelect = (props: UseSelectProps): UseSelectReturn => {
  const { options, isOpen, defaultValue, onChange } = props;

  /**
   * Used for open and close process for the dropdown list
   */
  const { onClose } = useDisclosure();

  /**
   * Query used to filter list
   */
  const [query, setQuery] = useState<string | null>(null);

  /**
   * Options used inner the hook to avoid edit the original
   */
  const innerOptions = useMemo(() => {
    return options;
  }, [options]);

  /**
   * Options filtered when user search
   */
  const filteredOptions = useMemo(() => {
    if (query) {
      return innerOptions.filter((option) => {
        const searchString = query?.toLowerCase();

        // Check in main label
        if (option?.label) {
          if (
            searchString
              .split(' ')
              .every((word: string) =>
                option?.label?.toLowerCase().includes(word)
              )
          ) {
            return true;
          }
        }

        // Check in alternates
        if (option.alternates) {
          for (const alternate of option.alternates) {
            if (
              alternate &&
              searchString
                .split(' ')
                .every((word: string) => alternate.toLowerCase().includes(word))
            ) {
              return true;
            }
          }
        }

        // If no match in main label or alternates, return false
        return false;
      });
    }

    return options;
  }, [options, query]);

  /**
   * Used to return the selection (onChange)
   */
  const [selectedOptions, setSelectedOptions] = useState<Option>({});

  /**
   * Reference of the DOM element used for the selection
   */
  const inputRef = useRef<any>(null);

  /**
   * set default value
   */
  const setDefaultValue = () => {
    if (defaultValue) {
      const option = options.find(({ value }) => value === defaultValue);
      if (option) {
        setSelectedOptions(option);
      }
    }
  };

  /**
   * Process to select option
   */
  const selectOption = (option: Option) => {
    setSelectedOptions(option);
    onClose();
    if (inputRef.current) {
      inputRef.current.value = option.label;
    }
  };

  /**
   * Check if option is already selected
   */
  const isSelected = (option: Option) => {
    if (Array.isArray(selectedOptions)) {
      return selectedOptions.some((opt) => option.value === opt.value);
    }
    return false;
  };

  /**
   * Clear selection and clear field value
   */
  const clear = (keepFieldValue = false) => {
    setSelectedOptions({});

    if (!keepFieldValue && inputRef.current) {
      inputRef.current.value = null;
      onChange && onChange('');
    }
  };

  /**
   * Handle focus on input
   */

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (selectedOptions?.label) {
      e.target.value = selectedOptions?.label;
    }
  };

  /**
   * Clear selection when dropdown is closed
   */
  useMemo(() => {
    if (!isOpen && query !== null) {
      setQuery('');
      setDefaultValue();
    }
  }, [isOpen]);

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && filteredOptions.length > 0) {
      // Handle Arrow Down key to navigate down in the filtered list
      event.preventDefault();
      const newIndex =
        focusedIndex === null
          ? 0
          : Math.min(focusedIndex + 1, filteredOptions.length - 1);
      setFocusedIndex(newIndex);
    } else if (event.key === 'ArrowUp' && filteredOptions.length > 0) {
      // Handle Arrow Up key to navigate up in the filtered list
      event.preventDefault();
      const newIndex =
        focusedIndex === null ? 0 : Math.max(focusedIndex - 1, 0);
      setFocusedIndex(newIndex);
    } else if (event.key === 'Enter' && focusedIndex !== null) {
      // Handle Enter key to select the focused option
      event.preventDefault();
      selectOption(filteredOptions[focusedIndex]);
      setFocusedIndex(null); // Reset focused index after selecting
      inputRef.current.blur(); // Blur the input after selecting an option
    }
  };

  /**
   get the default value from the options
    */
  useMemo(() => {
    setDefaultValue();
  }, [defaultValue]);

  return {
    onClose,
    setQuery,
    selectOption,
    selectedOptions,
    filteredOptions,
    inputRef,
    isSelected,
    clear,
    handleKeyDown,
    focusedIndex,
    onFocus,
  };
};
